import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '@/App';
import { getExamReview } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, CheckCircle, XCircle, Clock, Award } from 'lucide-react';

export default function ExamReview() {
  const { examId } = useParams();
  const { currentStudent } = useAppContext();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAttempt, setSelectedAttempt] = useState(0);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getExamReview(examId, currentStudent.id);
      setData(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [examId, currentStudent?.id]);

  // The data loader is memoized; CRA's lint stack still flags this pattern here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (currentStudent?.id) loadData();
  }, [currentStudent?.id, loadData]);

  if (loading) return <div className="h-96 bg-white rounded-xl animate-pulse" />;
  if (!data) return <p className="text-center py-16 text-[#062B1A]/50">No review data</p>;

  const exam = data.exam;
  const questions = data.questions || [];
  const attempts = data.attempts || [];
  const currentAttemptData = attempts[selectedAttempt];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/student/exams')} className="text-[#062B1A]/60">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Exam Review</h2>
          <p className="text-sm text-[#062B1A]/60">{exam.title}</p>
        </div>
      </div>

      {/* Attempt Selector + Score Summary */}
      {attempts.length > 0 && (
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {attempts.length > 1 && (
                  <div className="flex gap-2">
                    {attempts.map((att, idx) => (
                      <Button
                        key={idx}
                        size="sm"
                        variant={selectedAttempt === idx ? 'default' : 'outline'}
                        className={selectedAttempt === idx ? 'bg-[#062B1A] text-white' : 'border-[rgba(6, 43, 26,0.12)]'}
                        onClick={() => setSelectedAttempt(idx)}
                      >
                        Attempt {idx + 1}
                      </Button>
                    ))}
                  </div>
                )}
                {attempts.length === 1 && <span className="text-sm text-[#062B1A]/60">1 attempt</span>}
              </div>
              {currentAttemptData && (
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#D4AF37]" style={{ fontFamily: 'var(--font-display)' }}>
                    {currentAttemptData.score}/{currentAttemptData.max_score}
                  </p>
                  <p className="text-xs text-[#062B1A]/40">
                    {currentAttemptData.max_score > 0 ? Math.round((currentAttemptData.score / currentAttemptData.max_score) * 100) : 0}% correct
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {attempts.length === 0 && (
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardContent className="p-6 text-center">
            <Award className="w-10 h-10 text-[#062B1A]/15 mx-auto mb-2" />
            <p className="text-[#062B1A]/50">You haven't attempted this exam yet</p>
            <Button className="mt-3 bg-[#062B1A] text-white hover:bg-[#041B10]" size="sm" onClick={() => navigate(`/student/exams/${examId}/take`)}>Take Exam</Button>
          </CardContent>
        </Card>
      )}

      {/* Question-by-question review */}
      {currentAttemptData && (
        <div className="space-y-4">
          <h3 className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Question Review</h3>
          {questions.map((q, idx) => {
            const studentAnswer = currentAttemptData.answers?.[String(idx)];
            const isShortAnswer = q.type === 'short_answer';
            // Marked the same way the server marked it, so the review never
            // disagrees with the score: an index for chosen options, a
            // case- and space-insensitive match for typed answers.
            const normalize = (value) => String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
            const isCorrect = studentAnswer === undefined
              ? false
              : (isShortAnswer
                ? (q.accepted_answers || []).some((answer) => normalize(answer) && normalize(answer) === normalize(studentAnswer))
                : studentAnswer === q.correct_answer);
            return (
              <Card key={idx} className={`border shadow-sm ${isCorrect ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    {isCorrect ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <Badge className={`text-[10px] border-0 ${isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {isCorrect ? 'Correct' : 'Incorrect'} • {q.marks} marks
                    </Badge>
                    <span className="text-xs text-[#062B1A]/40">Q{idx + 1}</span>
                  </div>
                  <p className="text-sm font-medium text-[#062B1A] mb-4">{q.text}</p>
                  <div className="space-y-2">
                    {isShortAnswer ? (
                      <>
                        <div className={`rounded-lg border p-3 text-sm ${
                          isCorrect ? 'border-green-300 bg-green-50 text-green-800' : 'border-red-300 bg-red-50 text-red-800'
                        }`}>
                          <p className="text-[10px] font-medium uppercase tracking-[0.08em] opacity-70">Your answer</p>
                          <p className="mt-1 whitespace-pre-wrap">{studentAnswer ?? <span className="italic opacity-60">Not answered</span>}</p>
                        </div>
                        {!isCorrect && (q.accepted_answers || []).length > 0 && (
                          <div className="rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-800">
                            <p className="text-[10px] font-medium uppercase tracking-[0.08em] opacity-70">Accepted answers</p>
                            <p className="mt-1">{(q.accepted_answers || []).join(' · ')}</p>
                          </div>
                        )}
                      </>
                    ) : q.options.map((opt, oIdx) => {
                      const isStudentChoice = studentAnswer === oIdx;
                      const isCorrectChoice = q.correct_answer === oIdx;
                      return (
                        <div
                          key={oIdx}
                          className={`p-3 rounded-lg border text-sm ${
                            isCorrectChoice
                              ? 'border-green-300 bg-green-50 text-green-800'
                              : isStudentChoice && !isCorrectChoice
                                ? 'border-red-300 bg-red-50 text-red-800'
                                : 'border-[rgba(6, 43, 26,0.08)] text-[#062B1A]/70'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {isCorrectChoice && <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />}
                            {isStudentChoice && !isCorrectChoice && <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                            <span>{opt}</span>
                            {isCorrectChoice && <span className="text-[10px] ml-auto text-green-600 font-medium">Correct Answer</span>}
                            {isStudentChoice && !isCorrectChoice && <span className="text-[10px] ml-auto text-red-500 font-medium">Your Answer</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Written to be read after the paper is handed in — the
                      server withholds it from the sitting candidate. */}
                  {q.explanation && (
                    <div className="mt-3 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3">
                      <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#062B1A]/45">Explanation</p>
                      <p className="mt-1 text-sm text-[#062B1A]/75">{q.explanation}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
